import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { FiUser, FiLock } from 'react-icons/fi';
import { athleteAPI } from '../../services/api';
import { hasPlanOrHigher, isSubscriptionEntitled } from '../../utils/subscriptionUtils';
import {
  DashboardContainer,
  BurgerButton,
  MobileDashboardHeader,
  MobileHeaderBurger,
  MobileHeaderLogo,
  MobileHeaderHelp,
  MobileHeaderAddButton,
  MobileHeaderAddMenu,
  MobileHeaderAddOption,
  SidebarOverlay,
  Sidebar,
  SidebarTop,
  SidebarLogoButton,
  SidebarLogoSJ,
  SidebarLogoMate,
  SidebarTitle,
  SidebarDivider,
  NavList,
  NavItem,
  NavSubItem,
  NavSubList,
  NavSpacer,
  SidebarBottom,
  LogoutButton,
  SettingsIconButton,
  SidebarFooterLine,
  MainContent,
} from './styles';
import styled from 'styled-components';
import TrainingSessions from '../TrainingSessions/TrainingSessions';
import CompetitionSessions from '../CompetitionSessions/CompetitionSessions';
import Athletes from '../Athletes/Athletes';
import CoachHome from '../CoachHome/CoachHome';
import Equipment from '../Equipment/Equipment';
import AthleteDetail from '../Athletes/AthleteDetail';
import TrainingPlanner from '../TrainingPlanner/TrainingPlanner';
import BananaCup from '../BananaCup/BananaCup';
import AccountSettings from '../AccountSettings/AccountSettings';
import Analytics from '../Analytics/Analytics';
import CoachChat from '../Chat/CoachChat';
import LoadingSpinner from '../Common/LoadingSpinner';

const UserIcon = FiUser as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const LockIcon = FiLock as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const LockOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 320px;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  overflow-y: auto;

  @media (max-width: 768px) {
    left: 0;
    z-index: 50; /* below BurgerButton (101) and Sidebar (100) so user can open menu */
  }

  @media (max-width: 1024px) and (orientation: landscape) {
    z-index: 50;
  }
`;

const LockContent = styled.div`
  text-align: center;
  max-width: 500px;
`;

const LockIconLarge = styled.div`
  font-size: 5rem;
  color: var(--accent-orange);
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LockTitle = styled.h2`
  font-size: 2rem;
  font-weight: 900;
  color: var(--text-dark);
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: -0.02em;
`;

const LockText = styled.p`
  font-size: 1.1rem;
  color: var(--text-gray);
  margin-bottom: 2rem;
  font-family: 'Poppins', sans-serif;
  line-height: 1.6;
`;

const LockButton = styled.button`
  padding: 1rem 2.5rem;
  background: var(--accent-orange);
  color: var(--text-light);
  border: none;
  border-radius: var(--radius-full);
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(255, 150, 0, 0.3);

  &:hover {
    background: var(--accent-orange-dark);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(255, 150, 0, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const BlurredContent = styled.div<{ $isLocked: boolean }>`
  filter: ${props => props.$isLocked ? 'blur(4px)' : 'none'};
  transition: filter 0.3s;
  width: 100%;
  min-height: 100%;
`;

type NavSection = {
  id: string;
  label: string;
  path: string;
  subItems?: NavSection[];
};

const navSections: NavSection[] = [
  { id: 'home', label: 'Home', path: '/coach/home' },
  { id: 'athletes', label: 'Athletes', path: '/coach/athletes' },
  { id: 'training-sessions', label: 'Training Sessions', path: '/coach/training-sessions' },
  { id: 'competition-sessions', label: 'Competition Sessions', path: '/coach/competition-sessions' },
  { id: 'equipment', label: 'Equipment', path: '/coach/equipment' },
  { id: 'training-planner', label: 'Training Planner', path: '/coach/training-planner' },
  { id: 'banana-cup', label: 'Banana Cup 🍌', path: '/coach/banana-cup' },
  { id: 'analytics', label: 'Analytics', path: '/coach/analytics' },
];

const CoachDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { canAccessPage, isLoading: isSubscriptionLoading } = useSubscriptionAccess();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>(() => {
    // Don't set active section if we're on account-settings page
    if (location.pathname === '/coach/account-settings') {
      return '';
    }
    // Check for AI chat first
    if (location.pathname === '/coach/ai-chat') {
      return 'ai-chat';
    }
    // Check for analytics
    if (location.pathname.startsWith('/coach/analytics')) {
      return 'analytics';
    }
    const match = navSections.find(section => location.pathname.includes(section.id));
    return match ? match.id : 'training-sessions';
  });

  useEffect(() => {
    // Don't set active section if we're on account-settings page
    if (location.pathname === '/coach/account-settings') {
      setActiveSection('');
      return;
    }
    // Check for AI chat first
    if (location.pathname === '/coach/ai-chat') {
      setActiveSection('ai-chat');
      return;
    }
    // Check for analytics
    if (location.pathname.startsWith('/coach/analytics')) {
      setActiveSection('analytics');
      return;
    }
    const match = navSections.find(section => location.pathname.includes(section.id));
    setActiveSection(match ? match.id : 'training-sessions');
  }, [location.pathname]);

  const handleNavigation = (section: NavSection) => {
    setActiveSection(section.id);
    navigate(section.path);
    // Close sidebar on mobile after navigation
    setIsSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsSidebarOpen(false);
  };

  const handleSettingsClick = () => {
    navigate('/coach/account-settings');
    setIsSidebarOpen(false);
  };

  const handleMobileAddOption = (action: 'athlete' | 'training' | 'planner' | 'competition' | 'equipment' | 'banana') => {
    setMobileAddMenuOpen(false);
    setIsSidebarOpen(false);
    if (action === 'athlete') {
      navigate('/coach/athletes', { state: { openAddAthlete: true } });
      return;
    }
    if (action === 'training') {
      navigate('/coach/training-sessions', { state: { openNewSessionForm: true } });
      return;
    }
    if (action === 'planner') {
      navigate('/coach/training-planner', { state: { openPlanNewSession: true } });
      return;
    }
    if (action === 'competition') {
      navigate('/coach/competition-sessions', { state: { openNewCompetitionForm: true } });
      return;
    }
    if (action === 'equipment') {
      navigate('/coach/equipment', { state: { openAddEquipment: true } });
      return;
    }
    if (action === 'banana') {
      navigate('/coach/banana-cup', { state: { openNewLeagueForm: true } });
    }
  };

  const isSettingsActive = location.pathname === '/coach/account-settings';

  // Close sidebar when clicking overlay
  const handleOverlayClick = () => {
    setIsSidebarOpen(false);
  };

  // Determine if current page is locked
  const getCurrentPageId = (): string | null => {
    if (location.pathname === '/coach/ai-chat') return 'ai-chat';
    if (location.pathname.startsWith('/coach/analytics')) return 'analytics';
    if (location.pathname === '/coach/account-settings') return null; // Settings always accessible
    if (location.pathname.startsWith('/coach/athletes/')) return null; // Athlete detail always accessible
    const match = navSections.find(section => location.pathname.includes(section.id));
    return match ? match.id : null;
  };

  const currentPageId = getCurrentPageId();
  // Only check if page is locked after subscription data has loaded
  const isCurrentPageLocked = !isSubscriptionLoading && currentPageId ? !canAccessPage(currentPageId) : false;

  // Mobile header visibility on scroll (mobile only)
  const [mobileHeaderVisible, setMobileHeaderVisible] = useState(true);
  // Mobile header: add menu open (replaces ? with + on mobile)
  const [mobileAddMenuOpen, setMobileAddMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  const [addMenuAthletes, setAddMenuAthletes] = useState<{ subscription?: { plan?: string } }[]>([]);
  const lastScrollYRef = React.useRef(0);

  // Fetch athletes for subscription-gated add menu (mobile header + coach home)
  useEffect(() => {
    let cancelled = false;
    athleteAPI.getAll().then((list) => {
      if (!cancelled) setAddMenuAthletes(list);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const hasAnyPaidPlan = addMenuAthletes.some((a: any) => {
    if (!isSubscriptionEntitled(a.subscription)) return false;
    return hasPlanOrHigher((a.subscription?.plan as string) || 'free', 'hs60');
  });
  const hasBananaCupAccess = addMenuAthletes.some((a: any) => {
    if (!isSubscriptionEntitled(a.subscription)) return false;
    return hasPlanOrHigher((a.subscription?.plan as string) || 'free', 'hs90');
  });

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [scrollContainer, setScrollContainer] = React.useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!isMobileView || !scrollContainer) return;
    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      if (scrollTop > 60 && scrollTop > lastScrollYRef.current) {
        setMobileHeaderVisible(false);
      } else if (scrollTop <= 60 || scrollTop < lastScrollYRef.current) {
        setMobileHeaderVisible(true);
      }
      lastScrollYRef.current = scrollTop;
    };
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [isMobileView, scrollContainer]);

  const isCoachHome = location.pathname === '/coach/home';

  // Prevent body scroll when overlay is shown and lock scroll position
  useEffect(() => {
    if (isCurrentPageLocked || isSubscriptionLoading) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isCurrentPageLocked, isSubscriptionLoading]);

  return (
    <DashboardContainer>
      {/* Mobile-only header: burger, logo, + (add menu; replaces ? so user can add from any page) */}
      {isMobileView && (
        <MobileDashboardHeader $visible={mobileHeaderVisible}>
          <MobileHeaderBurger
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </MobileHeaderBurger>
          <MobileHeaderLogo
            onClick={() => {
              navigate('/coach/home');
              setIsSidebarOpen(false);
            }}
            aria-label="Go to dashboard home"
          >
            <SidebarLogoSJ as="span">sj</SidebarLogoSJ>
            <SidebarLogoMate as="span">Mate</SidebarLogoMate>
          </MobileHeaderLogo>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <MobileHeaderAddButton
              type="button"
              onClick={() => setMobileAddMenuOpen(prev => !prev)}
              title={mobileAddMenuOpen ? 'Close menu' : 'Add new'}
              aria-label={mobileAddMenuOpen ? 'Close menu' : 'Add new'}
            >
              +
            </MobileHeaderAddButton>
            {mobileAddMenuOpen && (
              <MobileHeaderAddMenu>
                <MobileHeaderAddOption onClick={() => handleMobileAddOption('athlete')}>Add new athlete</MobileHeaderAddOption>
                <MobileHeaderAddOption onClick={() => handleMobileAddOption('training')}>Add new training session</MobileHeaderAddOption>
                {hasAnyPaidPlan && (
                  <>
                    <MobileHeaderAddOption onClick={() => handleMobileAddOption('competition')}>Add new competition session</MobileHeaderAddOption>
                    <MobileHeaderAddOption onClick={() => handleMobileAddOption('equipment')}>Add new equipment</MobileHeaderAddOption>
                  </>
                )}
                {hasBananaCupAccess && (
                  <>
                    <MobileHeaderAddOption onClick={() => handleMobileAddOption('planner')}>Plan new training session</MobileHeaderAddOption>
                    <MobileHeaderAddOption onClick={() => handleMobileAddOption('banana')}>Create new Banana Cup</MobileHeaderAddOption>
                  </>
                )}
              </MobileHeaderAddMenu>
            )}
          </div>
        </MobileDashboardHeader>
      )}
      {!isMobileView && (
        <BurgerButton
          $isOpen={isSidebarOpen}
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </BurgerButton>
      )}
      <SidebarOverlay $isOpen={isSidebarOpen} onClick={handleOverlayClick} />
      <Sidebar $isOpen={isSidebarOpen}>
        <SidebarTop>
          <SidebarLogoButton
            onClick={() => {
              navigate('/coach/home');
              setIsSidebarOpen(false);
            }}
            type="button"
            aria-label="Go to dashboard home"
          >
            <SidebarLogoSJ>sj</SidebarLogoSJ>
            <SidebarLogoMate>Mate</SidebarLogoMate>
          </SidebarLogoButton>
          <SidebarTitle>COACH DASHBOARD</SidebarTitle>
          <SidebarDivider />
        </SidebarTop>
        <NavList $gap="0.6rem">
          {['home', 'athletes', 'training-sessions', 'competition-sessions', 'equipment', 'training-planner', 'banana-cup', 'analytics'].map((id, index) => {
            const section = navSections.find(s => s.id === id);
            if (!section) return null;
            const isFirstItem = index === 0;
            const hasAccess = canAccessPage(section.id);
            return (
              <NavItem
                key={section.id}
                $active={activeSection === section.id}
                $isFirstItem={isFirstItem}
                onClick={() => handleNavigation(section)}
                style={{
                  opacity: hasAccess ? 1 : 0.5,
                  cursor: hasAccess ? 'pointer' : 'not-allowed',
                  position: 'relative'
                }}
              >
                {!hasAccess && (
                  <LockIcon style={{
                    position: 'absolute',
                    right: '1rem',
                    fontSize: '0.9rem',
                    color: 'var(--accent-orange)'
                  }} />
                )}
                {section.label}
              </NavItem>
            );
          })}
          <NavItem
            $active={activeSection === 'ai-chat'}
            $isFirstItem={false}
            onClick={() => {
              if (!canAccessPage('ai-chat')) return;
              navigate('/coach/ai-chat');
              setIsSidebarOpen(false);
            }}
            style={{
              opacity: canAccessPage('ai-chat') ? 1 : 0.5,
              cursor: canAccessPage('ai-chat') ? 'pointer' : 'not-allowed',
              position: 'relative'
            }}
          >
            {!canAccessPage('ai-chat') ? (
              <LockIcon style={{ 
                position: 'absolute', 
                right: '1rem', 
                fontSize: '0.9rem',
                color: 'var(--accent-orange)'
              }} />
            ) : null}
            AI MATE
          </NavItem>
          <NavSpacer />
        </NavList>
        <SidebarBottom>
          <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
          <SettingsIconButton 
            onClick={handleSettingsClick} 
            title="Account Settings"
            style={{
              borderColor: isSettingsActive ? 'var(--accent-orange)' : 'var(--card-border)',
              color: isSettingsActive ? 'var(--accent-orange)' : 'var(--text-gray)',
              background: isSettingsActive ? 'var(--bg-sidebar)' : 'var(--bg-primary)',
            }}
          >
            <UserIcon />
          </SettingsIconButton>
        </SidebarBottom>
        <SidebarFooterLine />
      </Sidebar>
      <MainContent ref={(el) => setScrollContainer(el)} style={{ position: 'relative', overflow: (isCurrentPageLocked || isSubscriptionLoading) ? 'hidden' : 'auto' }}>
        <BlurredContent $isLocked={isCurrentPageLocked}>
          <Routes>
            <Route path="home" element={<CoachHome />} />
            <Route path="training-sessions" element={<TrainingSessions />} />
            <Route path="athletes" element={<Athletes />} />
            <Route path="competition-sessions" element={<CompetitionSessions />} />
            <Route path="equipment" element={<Equipment />} />
            <Route path="training-planner" element={<TrainingPlanner />} />
            <Route path="banana-cup" element={<BananaCup />} />
            <Route path="analytics/*" element={<Analytics />} />
            <Route path="ai-chat" element={<CoachChat />} />
            <Route path="account-settings" element={<AccountSettings />} />
            <Route path="athletes/:id" element={<AthleteDetail />} />
            <Route path="*" element={<CoachHome />} />
          </Routes>
        </BlurredContent>
        {isSubscriptionLoading && (
          <LockOverlay>
            <LoadingSpinner />
          </LockOverlay>
        )}
        {!isSubscriptionLoading && isCurrentPageLocked && (
          <LockOverlay>
            <LockContent>
              <LockIconLarge>
                <LockIcon style={{ fontSize: '5rem' }} />
              </LockIconLarge>
              <LockTitle>
                {currentPageId === 'ai-chat' ? 'Coming Soon' : 'This Feature is Locked'}
              </LockTitle>
              <LockText>
                {currentPageId === 'ai-chat'
                  ? 'AI Mate is not quite ready yet. In the meantime, check out our other plans on the Pricing page.'
                  : currentPageId === 'analytics'
                  ? 'Upgrade to HS130 or HS240 plan to unlock Analytics and track your performance with detailed insights.'
                  : (currentPageId === 'training-planner' || currentPageId === 'equipment')
                  ? 'Upgrade to HS90, HS130, or HS240 plan to unlock this feature and access advanced training tools.'
                  : 'Upgrade to HS60, HS90, HS130, or HS240 plan to unlock this feature and access advanced training tools.'
                }
              </LockText>
              <LockButton onClick={() => navigate('/pricing')}>
                View Pricing Plans
              </LockButton>
            </LockContent>
          </LockOverlay>
        )}
      </MainContent>
    </DashboardContainer>
  );
};

export default CoachDashboard; 